import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Coins, Loader2, Gift, Trophy, Sparkles, Clock, X } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { CLP } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

export const FICHAS_EVENT = "fichas:changed";
export const emitFichas = () => window.dispatchEvent(new Event(FICHAS_EVENT));
const fmtDate = (iso) => new Date(iso).toLocaleDateString("es-CL", { timeZone: "America/Santiago", day: "2-digit", month: "2-digit", year: "numeric" }).replace(/-/g, "/");
const fmtUF = (v) => String(v).replace(".", ",");

export function useFichas() {
  const [data, setData] = useState(null);
  useEffect(() => {
    const load = () => api.get("/business/fichas").then((r) => setData(r.data)).catch(() => {});
    load();
    window.addEventListener(FICHAS_EVENT, load);
    return () => window.removeEventListener(FICHAS_EVENT, load);
  }, []);
  return data;
}

export function PendingReminder({ onGo }) {
  const f = useFichas();
  const [hidden, setHidden] = useState(() => sessionStorage.getItem("vt-pending-reminder") === "1");
  if (!f || hidden || f.balance < 1 || !f.pending_count) return null;
  const n = f.pending_count;
  const close = () => { sessionStorage.setItem("vt-pending-reminder", "1"); setHidden(true); };
  return (
    <div className="mb-4 flex flex-wrap items-center gap-3 rounded-2xl border border-amber-300 bg-amber-50 px-4 py-3 text-amber-900 animate-in fade-in slide-in-from-top-2 duration-300" data-testid="pending-reminder">
      <Coins className="w-5 h-5 text-amber-600 shrink-0" />
      <p className="text-sm flex-1 min-w-[200px]" data-testid="pending-reminder-text">
        Tienes <strong>{n} taller{n === 1 ? "" : "es"} sin publicar</strong> y <strong>{f.balance} ficha{f.balance === 1 ? "" : "s"}</strong> disponible{f.balance === 1 ? "" : "s"}. ¡Publíca{n === 1 ? "lo" : "los"} para que los alumnos {n === 1 ? "lo" : "los"} vean!
      </p>
      <Button size="sm" onClick={onGo} data-testid="pending-reminder-go">Ver talleres</Button>
      <button type="button" onClick={close} aria-label="Cerrar aviso" className="grid place-items-center w-7 h-7 rounded-full hover:bg-amber-100" data-testid="pending-reminder-close"><X className="w-4 h-4" /></button>
    </div>
  );
}

export function FichasChip({ onClick }) {
  const f = useFichas();
  if (!f) return null;
  return (
    <button type="button" onClick={onClick} data-testid="fichas-chip"
      className="ml-auto inline-flex items-center gap-1.5 rounded-full border border-amber-300 bg-amber-50 px-3 py-1.5 text-sm font-semibold text-amber-800 hover:bg-amber-100 transition-colors shrink-0">
      <Coins className="w-4 h-4" />Saldo: <span data-testid="fichas-balance">{f.balance}</span> Ficha{f.balance === 1 ? "" : "s"}
    </button>
  );
}

function goToWebpay({ url, token }) {
  const form = document.createElement("form");
  form.method = "POST"; form.action = url;
  const input = document.createElement("input");
  input.type = "hidden"; input.name = "token_ws"; input.value = token;
  form.appendChild(input); document.body.appendChild(form); form.submit();
}

const RESULT = {
  approved: ["success", "¡Compra aprobada! Tus Fichas ya están en tu billetera."],
  rejected: ["error", "El pago fue rechazado. No se cargaron fichas."],
  aborted: ["error", "El pago no se completó. No se cargaron fichas."],
  timeout: ["error", "El pago expiró. No se cargaron fichas."],
  error: ["error", "No pudimos confirmar el pago. Inténtalo nuevamente."],
};

function PackCard({ p, onBuy, busy, picked }) {
  const best = picked ? p.id === picked : p.id === "pro";
  return (
    <div id={`pack-card-${p.id}`} className={`relative rounded-2xl border p-4 flex flex-col transition-shadow ${best ? "border-primary ring-1 ring-primary/30" : ""} ${picked === p.id ? "ring-2 shadow-lg" : ""}`} data-testid={`pack-${p.id}`}>
      {picked === p.id && <span className="absolute -top-2.5 left-3 rounded-full bg-primary px-2 py-0.5 text-[10px] font-bold text-primary-foreground" data-testid={`pack-picked-${p.id}`}>Plan elegido</span>}
      {p.saving_pct > 0 && <span className="absolute -top-2.5 right-3 rounded-full bg-coral px-2 py-0.5 text-[10px] font-bold text-white">{p.saving_pct}% ahorro</span>}
      <p className="font-bold">{p.name}</p>
      <p className="mt-1 text-2xl font-extrabold flex items-center gap-1"><Coins className="w-5 h-5 text-amber-500" />{p.fichas}<span className="text-sm font-medium text-muted-foreground">ficha{p.fichas > 1 ? "s" : ""}</span></p>
      {p.fichas > p.paid && <p className="text-xs text-primary font-semibold">{p.paid} + {p.fichas - p.paid} gratis</p>}
      {p.discount_pct > 0 && <p className="mt-2 text-xs" data-testid={`pack-discount-${p.id}`}><span className="line-through text-muted-foreground">{fmtUF(p.list_price_uf)} UF · {CLP(p.list_total_clp)}</span> <span className="ml-1 rounded-full bg-emerald-100 text-emerald-700 px-1.5 py-0.5 font-bold">-{p.discount_pct}%</span></p>}
      <p className={`${p.discount_pct > 0 ? "mt-1" : "mt-2"} text-sm`}><strong>{fmtUF(p.price_uf)} UF</strong> + IVA</p>
      <p className="text-xs text-muted-foreground" data-testid={`pack-breakdown-${p.id}`}>Neto {CLP(p.price_clp)} + IVA {p.iva_pct}% {CLP(p.iva_clp)}</p>
      <p className="text-sm font-bold text-primary" data-testid={`pack-total-${p.id}`}>Total {CLP(p.total_clp)}</p>
      <p className="text-xs text-muted-foreground">{fmtUF(p.unit_uf)} UF + IVA por ficha</p>
      <p className="text-xs text-muted-foreground mt-1 flex-1">{p.note}</p>
      <Button className="mt-3 w-full" variant={best ? "default" : "outline"} disabled={!!busy} onClick={() => onBuy(p.id)} data-testid={`buy-pack-${p.id}`}>
        {busy === p.id ? <Loader2 className="w-4 h-4 animate-spin" /> : "Comprar"}
      </Button>
    </div>
  );
}

function DiscountCountdown({ f }) {
  const d = f.discount_days_left;
  if (d === null || d === undefined) {
    return <p className="text-xs font-semibold text-emerald-700 mt-2" data-testid="wallet-discount-note">Tienes un descuento de {f.discount_pct}% aplicado al valor de cada pack.</p>;
  }
  const urgent = d <= 5;
  return (
    <div className={`mt-3 flex items-center gap-3 rounded-2xl border-2 px-4 py-3 ${urgent ? "border-amber-400 bg-amber-50" : "border-violet-300 bg-violet-50"}`} data-testid="wallet-discount-countdown">
      <div className={`grid h-14 w-14 shrink-0 place-items-center rounded-xl text-center leading-none ${urgent ? "bg-amber-400 text-violet-900" : "bg-violet-600 text-white"}`}>
        <span><span className="block text-2xl font-extrabold" data-testid="wallet-discount-days">{d}</span><span className="text-[10px] font-semibold uppercase">{d === 1 ? "día" : "días"}</span></span>
      </div>
      <div className="min-w-0 text-sm">
        <p className="font-bold text-violet-800">{d === 0 ? "¡Hoy es el último día de tu descuento!" : `Te ${d === 1 ? "queda 1 día" : `quedan ${d} días`} de descuento`}</p>
        <p className="text-slate-600">{f.discount_pct}% en cada pack de Fichas hasta el {fmtDate(`${f.discount_until}T12:00:00`)}.{urgent ? " ¡Aprovecha ahora!" : ""}</p>
      </div>
    </div>
  );
}

function Bonuses({ f }) {
  const perf = f.performance;
  return (
    <div className="grid sm:grid-cols-2 gap-3" data-testid="fichas-bonuses">
      <div className="rounded-xl border p-3 flex gap-3"><Gift className="w-5 h-5 text-primary shrink-0" />
        <div className="text-sm"><p className="font-semibold">Bono de bienvenida</p>
          <p className="text-muted-foreground" data-testid="bonus-welcome">{f.welcome.received ? "Recibido: 1 ficha para tu primer taller." : "Completa tu perfil (nombre, comuna y reseña o profesión) y recibe 1 ficha gratis."}</p></div></div>
      <div className="rounded-xl border p-3 flex gap-3"><Trophy className="w-5 h-5 text-amber-500 shrink-0" />
        <div className="text-sm"><p className="font-semibold">Bono por desempeño</p>
          <p className="text-muted-foreground" data-testid="bonus-performance">Cada {perf.step} alumnos distintos que asistan (registro de asistencia por QR) entre todos tus talleres ganas 1 ficha. Llevas {perf.students}; la próxima al llegar a {perf.next_at}.</p></div></div>
    </div>
  );
}

export function FichasWallet() {
  const f = useFichas();
  const [busy, setBusy] = useState(null);
  const [picked, setPicked] = useState(null);
  useEffect(() => {
    const sp = new URLSearchParams(window.location.search);
    const pk = sp.get("pack");
    if (pk) {
      setPicked(pk);
      sp.delete("pack");
      window.history.replaceState(null, "", `${window.location.pathname}?${sp.toString()}`);
    }
  }, []);
  useEffect(() => {
    if (picked && f) { setTimeout(() => document.getElementById(`pack-card-${picked}`)?.scrollIntoView({ behavior: "smooth", block: "center" }), 150); }
  }, [picked, f]);
  useEffect(() => {
    const sp = new URLSearchParams(window.location.search);
    const r = RESULT[sp.get("fichas")];
    if (r) {
      toast[r[0]](r[1]);
      sp.delete("fichas");
      window.history.replaceState(null, "", `${window.location.pathname}?${sp.toString()}`);
    }
  }, []);
  const buy = async (pack) => {
    setBusy(pack);
    try {
      const { data } = await api.post("/business/fichas/checkout", { pack });
      if (data.free) { toast.success("Fichas cargadas en tu billetera con tu descuento del 100%."); window.location.reload(); return; }
      goToWebpay(data);
    }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); setBusy(null); }
  };
  if (!f) return <div className="py-10 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;
  return (
    <Card className="p-5 sm:p-6 space-y-5" id="vivofichas" data-testid="fichas-wallet">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <span className="grid place-items-center w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-amber-100 text-amber-600 shrink-0"><Coins className="w-7 h-7" /></span>
          <div className="min-w-0">
            <h3 className="font-bold text-lg">Mis Fichas</h3>
            <p className="text-sm text-muted-foreground">1 ficha = 1 publicación de taller. Duran 1 año desde que las recibes.</p>
          </div>
        </div>
      </div>
      {f.next_expiry && <p className="text-xs flex items-center gap-1.5 text-amber-700" data-testid="wallet-expiry"><Clock className="w-3.5 h-3.5" />{f.next_expiry_qty} ficha{f.next_expiry_qty === 1 ? "" : "s"} vence{f.next_expiry_qty === 1 ? "" : "n"} el {fmtDate(f.next_expiry)}</p>}
      <div>
        <p className="font-semibold mb-3 flex items-center gap-1.5"><Sparkles className="w-4 h-4 text-primary" />Compra fichas</p>
        {f.discount_pct > 0 && <div className="mb-4"><DiscountCountdown f={f} /></div>}
        <div className="grid sm:grid-cols-3 gap-4 pt-1">{f.packs.map((p) => <PackCard key={p.id} p={p} onBuy={buy} busy={busy} picked={picked} />)}</div>
        <p className="text-[11px] text-muted-foreground mt-2">Precios en UF más IVA (19%). Pagas en una plataforma de pago con tarjeta de débito, crédito o prepago; el monto cobrado corresponde a la opción seleccionada con IVA incluido. También puedes usar 1 ficha para destacar un taller {f.feature_days} días.</p>
      </div>
      <Bonuses f={f} />
      <div>
        <p className="font-semibold mb-2">Movimientos</p>
        {f.moves.length === 0 ? <p className="text-sm text-muted-foreground">Aún no tienes movimientos.</p> : (
          <div className="divide-y rounded-xl border max-h-64 overflow-y-auto" data-testid="fichas-moves">
            {f.moves.map((m) => (
              <div key={m.id} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
                <div className="min-w-0"><p className="truncate">{m.label}{m.ref && m.reason === "performance" ? ` · ${m.ref}` : ""}</p><p className="text-xs text-muted-foreground">{fmtDate(m.created_at)}</p></div>
                <span className={`font-bold ${m.delta > 0 ? "text-emerald-600" : "text-rose-600"}`}>{m.delta > 0 ? "+" : ""}{m.delta}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </Card>
  );
}
